'use client'

import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { useTranslations } from 'next-intl'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { FileIcon, PlusCircleIcon, SaveIcon, TramFrontIcon, XIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Separator } from '@/components/ui/separator'
import { Combobox } from '@/components/ui/combobox'
import { FileSelector } from '@/components/partials/files'
import { UnsavedChangesGuardDialog } from '@/components/partials'
import { CreateModelParams, ModelFileParams, ModelType, UpdateModelParams } from '@/types/model'
import { FileType } from '@/types/file'
import { useBrands, useUnsavedChangesGuard } from '@/hooks'
import { FormModeEnum } from '@/enums'
import { setValidationErrors } from '@/lib/utils'

type PropsType = {
  initialData?: ModelType | null
  onSubmit: (data: CreateModelParams | UpdateModelParams) => void
  onSubmitAndNew?: (data: CreateModelParams | UpdateModelParams) => void
  onCancel: () => void
  isLoading?: boolean
  mode: FormModeEnum
}

export type ModelFormRef = {
  handleValidationErrors: (error: any) => void
  reset: () => void
}

const ModelForm = forwardRef<ModelFormRef, PropsType>(
  ({ initialData, onSubmit, onSubmitAndNew, onCancel, isLoading = false, mode }, ref) => {
    const t = useTranslations()

    // State for files
    const [files, setFiles] = useState<FileType[]>(initialData?.files?.map(f => f.file) || [])

    // Wrapper function to handle type conversion for FileSelector onChange
    const handleFilesChange = (files: FileType | FileType[] | null) => {
      if (files === null) {
        setFiles([])
      } else if ('length' in files) {
        setFiles(files)
      } else {
        setFiles([files])
      }
    }

    // Fetch brands
    const { data: brandsData } = useBrands({ size: 999999 })

    const modelSchema = z.object({
      brandId: z.string().min(1, t('not_blank')),
      name: z.string().min(1, t('not_blank'))
    })

    type FormData = z.infer<typeof modelSchema>

    const form = useForm<FormData>({
      resolver: zodResolver(modelSchema),
      defaultValues: {
        brandId: initialData?.brand?.id || '',
        name: initialData?.name || ''
      }
    })
    const { isNavigationBlocked, confirmNavigation, cancelNavigation } = useUnsavedChangesGuard(form.formState.isDirty)

    useImperativeHandle(ref, () => ({
      handleValidationErrors: (error: any) => {
        setValidationErrors(error, form.setError)
      },
      reset: () => form.reset()
    }))

    // Sync files state when initialData changes
    useEffect(() => {
      if (initialData?.files) {
        setFiles(initialData.files.map(f => f.file))
      }
    }, [initialData])

    const formErrors = form.formState.errors

    // Prepare brand options for combobox
    const brandOptions = useMemo(() => {
      if (!brandsData?.items) {
        return []
      }
      return brandsData.items.map(brand => ({
        label: brand.name,
        value: brand.id,
        searchValue: brand.name
      }))
    }, [brandsData])

    const handleSubmit = (data: FormData) => {
      // Prepare files data
      const filesData: ModelFileParams[] = files.map((file, index) => ({
        fileId: file.id,
        order: index + 1
      }))

      const submissionData: CreateModelParams | UpdateModelParams = {
        ...data,
        files: filesData
      }

      onSubmit(submissionData)
    }

    const handleSubmitAndNew = (data: FormData) => {
      const filesData: ModelFileParams[] = files.map((file, index) => ({
        fileId: file.id,
        order: index + 1
      }))

      const submissionData: CreateModelParams | UpdateModelParams = {
        ...data,
        files: filesData
      }

      onSubmitAndNew?.(submissionData)
    }

    const isCreateModeWithSubmitAndNew = mode === FormModeEnum.CREATE && !!onSubmitAndNew

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TramFrontIcon className="h-5 w-5" />
            {mode === FormModeEnum.CREATE ? t('create_model') : t('edit_model')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(isCreateModeWithSubmitAndNew ? handleSubmitAndNew : handleSubmit)}
              className="space-y-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-1 xl:grid-cols-2">
                <FormField
                  control={form.control}
                  name="brandId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t('brand')}</FormLabel>
                      <FormControl>
                        <Combobox
                          options={brandOptions}
                          value={field.value}
                          onValueChange={field.onChange}
                          placeholder={t('select')}
                          searchPlaceholder={t('search')}
                          emptyText={t('not_found')}
                          clearable
                          error={!!formErrors.brandId}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t('name')}</FormLabel>
                      <FormControl>
                        <Input placeholder={t('name')} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <Separator />

              {/* Files */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <FileIcon className="h-5 w-5" />
                  <h3 className="font-semibold">{t('files')}</h3>
                </div>
                <div>
                  <FileSelector
                    multiple={true}
                    value={files}
                    onChange={handleFilesChange}
                    buttonLabel={t('select_files')}
                    buttonLabelChange={t('change_files')}
                    noSelectionText={t('no_files_selected')}
                  />
                </div>
              </div>

              <Separator />

              <div className="flex items-center justify-between gap-3 pt-1">
                <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
                  <XIcon />
                  <span className="hidden sm:inline">{t('cancel')}</span>
                </Button>
                <div className="flex items-center gap-2">
                  {isCreateModeWithSubmitAndNew && (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => form.handleSubmit(handleSubmit)()}
                      disabled={isLoading}>
                      <SaveIcon />
                      <span className="hidden sm:inline">{isLoading ? t('saving') : t('save')}</span>
                    </Button>
                  )}
                  <Button type="submit" disabled={isLoading}>
                    {isCreateModeWithSubmitAndNew ? <PlusCircleIcon /> : <SaveIcon />}
                    <span className="hidden sm:inline">
                      {isLoading ? t('saving') : isCreateModeWithSubmitAndNew ? t('save_and_add_new') : t(mode)}
                    </span>
                  </Button>
                </div>
              </div>
              <UnsavedChangesGuardDialog
                open={isNavigationBlocked}
                onConfirm={confirmNavigation}
                onCancel={cancelNavigation}
              />
            </form>
          </Form>
        </CardContent>
      </Card>
    )
  }
)

ModelForm.displayName = 'ModelForm'

export default ModelForm
